import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { get, post } from "../../services/api"; // Verify correct path relative to new file
import Select from "react-select";
import { toast } from "react-toastify";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";

import DropdownWithCustomValuesModal from "../../components/DropdownWithCustomValuesModal";

const AutoAssignmentSetup = () => {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const roleId = localStorage.getItem("roleId");

  // Only Org Admin (Role 2) should access this generally, but we'll assume access is handled by router/props
  // User context implies this page IS for Org Admin.

  const [assignmentRules, setAssignmentRules] = useState([]);
  
  // Dropdown Data
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assignees, setAssignees] = useState([]);

  // Modal State
  const [customDropdownName, setCustomDropdownName] = useState("");
  const [
    isCustomDropdownValuesModalVisible,
    setIsCustomDropdownValuesModalVisible,
  ] = useState(false);

  // Default Form Values
  const defaultValues = {
    id: 0,
    departmentId: null,
    categoryId: null,
    assigneeId: null,
    notificationEmail: "",
    organizationId: organizationId,
  };

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues,
  });

  const selectedDepartment = watch("departmentId");

  // Fetch Common Settings
  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      const mappedData = response?.commonSettingDetail?.map(item => ({
          value: item.id,
          label: item.value
      })) || [];

      setData(mappedData);
    } catch (error) {
      console.error(`Error fetching ${moduleName}:`, error);
    }
  };

  const handleCustomDropdownSetting = (dropdownName) => {
    setCustomDropdownName(dropdownName);
    setIsCustomDropdownValuesModalVisible(true);
  };

  // Fetch Assignees based on Department
  const fetchAssignees = async (departmentId) => {
      if(!departmentId) {
          setAssignees([]);
          return;
      }
      try {
          // api/HelpDesk/GetTicketAssigneeUsers?RoleId=2035&DepartmentId='selected dept'
          const response = await get(`HelpDesk/GetTicketAssigneeUsers?RoleId=2035&DepartmentId=${departmentId}`, {
             headers: { Authorization: `Bearer ${token}` }
          });
          // Assuming response is array of users or objects. Mapping might be needed.
          // Let's assume standard { id, name/userName } structure or similar
          // Adjust mapping based on usual user API responses
          const mappedData = response?.map(u => ({
              value: u.id,
              label: u.firstName + ' ' + u.lastName // or u.userName
          })) || [];
          setAssignees(mappedData);
      } catch (error) {
          console.error("Error fetching assignees:", error);
      }
  };

  // Effect to load initial dropdowns
  useEffect(() => {
    fetchCommonSettings("Department", setDepartments);
    fetchCommonSettings("ticketCategory", setCategories); 
    fetchAssignments();
  }, []);

  // Effect: Fetch Assignees when Department Changes
  useEffect(() => {
     if(selectedDepartment?.value) {
         fetchAssignees(selectedDepartment.value);
     } else {
         setAssignees([]);
         setValue("assigneeId", null);
     }
  }, [selectedDepartment]);

  // Fetch Grid Data
  const fetchAssignments = async () => {
    try {
      // GET /api/HelpDesk/GetAssignmentRulesAsync?OrganizationId
      const response = await get(`HelpDesk/GetAssignmentRulesAsync?OrganizationId=${organizationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssignmentRules(response || []);
    } catch (error) {
      console.error("Error fetching rules:", error);
    }
  };

  const onSubmit = async (data) => {
    const payload = {
        id: data.id || 0,
        departmentId: data.departmentId?.value,
        categoryId: data.categoryId?.value,
        assigneeId: data.assigneeId?.value,
        notificationEmail: data.notificationEmail,
        organizationId: organizationId,
    };

    try {
      const response = await post("HelpDesk/CreateAssignmentRuleAsync", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.isSuccess) {
        toast.success("Assignment rule saved successfully!");
        reset(defaultValues);
        fetchAssignments();
      } else {
        toast.error(response.message || "Failed to save rule.");
      }
    } catch (error) {
      console.error("Error saving rule:", error);
    }
  };

  const handleEdit = async (row) => {
      // GET /api/HelpDesk/GetAssignmentRuleByIdAsync?Id
      try {
          const response = await get(`HelpDesk/GetAssignmentRuleByIdAsync?Id=${row.id}`, { 
              headers: { Authorization: `Bearer ${token}` } 
          });
          
          const data = response; // Assuming response is the object itself or response.data
          if(data) {
              // We need to construct the Select objects.
              // Note: We might be missing logical labels if the IDs are all we have.
              // Ideally we check our existing departments/categories lists.
              
              // Helper to find label
              const findOpt = (opts, val) => opts.find(o => o.value === val);

              // If departments/categories not loaded yet, this might be tricky, 
              // but they load on mount.
              
              const deptOpt = findOpt(departments, data.departmentId);
              const catOpt = findOpt(categories, data.categoryId);
              
              // For assignee, we need to fetch specific list for that department first?
              // Or we force fetch it.
              // Changing department via setValue might trigger effect, but we also want to set value.
              
              // Let's set form values.
              reset({
                  id: data.id,
                  notificationEmail: data.notificationEmail,
                  organizationId: organizationId,
                  departmentId: deptOpt || { value: data.departmentId, label: data.departmentName || "Selected" },
                  categoryId: catOpt || { value: data.categoryId, label: data.categoryName || "Selected" },
                  // assigneeId: set later
              });
              
              // We need to ensure we have the assignees list for this department
              // Use fetchAssignees directly (the effect might double call but it's okay)
              if(data.departmentId) {
                 // Creating a local Fetch to ensure we can map the assignee
                 const usersResponse = await get(`HelpDesk/GetTicketAssigneeUsers?RoleId=2035&DepartmentId=${data.departmentId}`, {
                     headers: { Authorization: `Bearer ${token}` }
                 });
                 const mappedUsers = usersResponse?.map(u => ({
                    value: u.id,
                    label: u.firstName + ' ' + u.lastName 
                 })) || [];
                 setAssignees(mappedUsers);
                 
                 const assigneeOpt = mappedUsers.find(u => u.value === data.assigneeId);
                 setValue("assigneeId", assigneeOpt || { value: data.assigneeId, label: data.assigneeName || "Selected" });
              }
          }

      } catch (err) {
          console.error("Error editing:", err);
      }
  };

  const handleDelete = async (row) => {
      if(window.confirm("Are you sure you want to delete this record?")) {
          try {

              const response = await get(`HelpDesk/DeleteAssignmentRule?ruleId=${row.id}&OrganizationId=${organizationId}`, { 
              headers: { Authorization: `Bearer ${token}` } 
          });
              
              if(response) {
                  toast.success("Success!");
                  fetchAssignments();
              } else {
                  toast.error("Failed to delete");
              }

          } catch (e) {
              console.error(e);
          }
      }
  };

  const columns = [
    {
      header: "Department",
      accessorKey: "departmentName", // Check API response field names
    },
    {
      header: "Category",
      accessorKey: "categoryName",
    },
    {
      header: "Assignee",
      accessorKey: "assigneeName",
    },
    // {
    //   header: "Email",
    //   accessorKey: "notificationEmail",
    // },
    {
      header: "Action",
      accessorKey: "actions",
      size: 120,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
            <button
            type="button"
              className="btn p-0"
            onClick={() => handleEdit(row.original)}
            >
            <i className="fas fa-edit"></i>
            </button>
            <button
            type="button"
              className="btn p-0"
            onClick={() => handleDelete(row.original)}
            >
            <i className="fas fa-trash"></i>
            </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Auto Assignment Setup</h2>
      </div>

      <div className="row g-3">
        {/* Left Side: Form */}
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              {/* <h5 className="mb-3">{watch("id") ? 'Edit Rule' : 'Create New Rule'}</h5> */}
              <form className="row g-3 new-form-style" onSubmit={handleSubmit(onSubmit)}>
                
                {/* Department */}
                <div className="col-md-12">
                  <label className="form-label form-label-new">
                    Department <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="departmentId"
                    control={control}
                    rules={{ required: "This field is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={departments}
                        placeholder="Select"
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.departmentId && <small className="text-danger">{errors.departmentId.message}</small>}
                </div>

                {/* Assign To */}
                <div className="col-md-12">
                  <label className="form-label form-label-new">
                    Assign To <span className="text-danger">*</span>
                  </label>
                  <Controller
                    name="assigneeId"
                    control={control}
                    rules={{ required: "This field is required" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={assignees}
                        placeholder="Select"
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isDisabled={!selectedDepartment}
                      />
                    )}
                  />
                  {errors.assigneeId && <small className="text-danger">{errors.assigneeId.message}</small>}
                </div>

                {/* Category */}
                <div className="col-md-12">
                  <label className="form-label form-label-new">
                    Category <span className="text-danger">*</span>
                  </label>
                  <div className="d-flex gap-2">
                     <div className="w-100">
                        <Controller
                            name="categoryId"
                            control={control}
                            rules={{ required: "This field is required" }}
                            render={({ field }) => (
                            <Select
                                {...field}
                                options={categories}
                                placeholder="Select"
                                className="react-select-style"
                                classNamePrefix="react-select-style-pref"
                            />
                            )}
                        />
                        {errors.categoryId && <small className="text-danger">{errors.categoryId.message}</small>}
                     </div>
                     <div>
                        <button
                            type="button"
                            className="btn btn-outline-primary px-2 lh-1"
                            onClick={() => handleCustomDropdownSetting("ticketCategory")}
                        >
                            <i className="fas fa-plus"></i>
                        </button>
                    </div>
                  </div>
                </div>

                {/* Notification Email */}
                {/* <div className="col-md-12">
                  <label className="form-label form-label-new">
                    Notification Email
                  </label>
                  <Controller
                    name="notificationEmail"
                    control={control}
                    // Optional rules if needed for email format
                    render={({ field }) => (
                      <input
                        {...field}
                        type="email"
                        className="form-control"
                        placeholder="email@example.com"
                      />
                    )}
                  />
                </div> */}

                <div className="col-12 mt-4 text-end">
                   <button 
                     type="button" 
                     className="btn btn-outline-primary me-2 btn-sm" 
                     onClick={() => reset(defaultValues)}
                   >
                     Clear
                   </button>
                   <button 
                     type="submit" 
                     className="btn btn-primary btn-sm" 
                     disabled={isSubmitting}
                   >
                     {isSubmitting ? "Saving..." : "Save"}
                   </button>
                </div>

              </form>
              
              {/* Error Banner */}
              {Object.keys(errors).length > 0 && (
                <div className="alert alert-danger mt-3" role="alert">
                    Please fill all required fields.
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Right Side: Grid */}
        <div className="col-md-8">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={assignmentRules}
                loading={false} 
                initialState={{
                    columnPinning: { right: ["actions"] },
                  }}
              />
            </div>
          </div>
        </div>
      </div>
       {isCustomDropdownValuesModalVisible && (
       <DropdownWithCustomValuesModal
        dropdownName={customDropdownName}
        onClose={() => {
            setIsCustomDropdownValuesModalVisible(false);
            if(customDropdownName === "ticketCategory") {
                fetchCommonSettings("ticketCategory", setCategories);
            }
        }}
      />
      )}
    </>
  );
};

export default AutoAssignmentSetup;
